<template>
    <div>
        <page-header
            :text="$t('navigation.systems.parameter_logs')"
            :searchField="{
                placeholder: $t('systems.parameter_logs.search'),
                inputEmitEvent: 'search',
                allowedFields: searchFields,
                defaultField: 'component',
            }"
            :button="{
                text: $t('systems.parameter_logs.create_textentry'),
                emitEvent: 'createTextentry',
                ability: true,
            }"
            @createTextentry="createTextentryHandler"
            @search="searchHandler"
        />

        <page-subnavigation :items="allSystemLinks" />

        <div class="table-wrapper">
            <table class="table table-filter table--striped table--filterable" v-if="filteredLogs">
                <thead>
                    <tr>
                        <td width="10%">{{ $t('fields.function') }}</td>
                        <td width="20%">{{ $t('systems.parameter_logs.parameter') }}</td>
                        <td width="20%">{{ $t('systems.parameter_logs.value') }}</td>
                        <td width="15%">{{ $t('systems.parameter_logs.user') }}</td>
                        <td width="15%">{{ $t('systems.parameter_logs.date_and_time') }}</td>
                        <td width="15%">{{ $t('systems.parameter_logs.device') }}</td>
                    </tr>
                </thead>

                <tbody>
                    <tr v-for="log in filteredLogs" :key="log.created_at">
                        <td>{{ log.function_name }}</td>
                        <td>{{ log.parameter }}</td>
                        <td>
                            <span class="lazy-parameter" v-if="log.type == 'program'">
                                {{ log.value_formatted }}
                                <EditPopup icon="common-info-full" :disableEditButtons="true" :containerPadded="false">
                                    <div class="edit-popup__row">
                                        <lazy-parameter-program :value="log.data.value" :disabled="true"></lazy-parameter-program>
                                    </div>
                                </EditPopup>
                            </span>
                            <span v-else>{{ log.value_formatted }}</span>
                        </td>
                        <td>{{ log.user_name }}</td>
                        <td>{{ log.created_at_formatted }}</td>
                        <td>
                            <span v-if="log.device">
                                <strong>{{ log.device.name }}</strong> <br />
                                {{ log.device.model }}
                                {{ log.device.identifier ? log.device.identifier : '' }}
                            </span>
                        </td>
                    </tr>
                </tbody>

                <TablePagination
                    v-if="paginationData"
                    :paginationData="paginationData"
                    :visibleAmount="searchedLogs ? searchedLogs.length : logs.length"
                    @goToPrevPage="prevPageHandler"
                    @goToNextPage="nextPageHandler"
                />
            </table>
        </div>
    </div>
</template>

<script>
import * as moment from 'moment';
import forEach from 'lodash/forEach';
import { mapState } from 'vuex';
import * as api from '@/api';
import PageHeader from '@/components/PageHeader';
import TablePagination from '@/components/TablePagination';
import LazyParameterProgram from '@/components/LazyParameterProgram';
import Parameter from '@/helpers/parameter';
import debounce from 'lodash/debounce';
import { createSearchParams } from '@/utils/searchParameters';
import EditPopup from '@/components/EditPopup';
import PageSubnavigation from '@/components/PageSubnavigation.vue';
import generalLinks from '@/mixins/generalLinks';

export default {
    name: 'system-parameter-logs',
    components: {
        PageHeader,
        TablePagination,
        LazyParameterProgram,
        EditPopup,
        PageSubnavigation,
    },
    mixins: [generalLinks],
    data() {
        return {
            activeSearchTerm: false,
            logs: [],
            searchedLogs: false,
            structure: null,
            paginationData: false,
            searchFields: ['component', 'parameter', 'oid', 'user_name'],
        };
    },
    computed: {
        ...mapState(['activeSystem']),
        filteredLogs() {
            if (!this.logs) return [];

            let filtered = this.searchedLogs ? [...this.searchedLogs] : [...this.logs];
            return this.prepareLogs(filtered);
        },
        activePage() {
            return this.paginationData ? this.paginationData.current_page : 0;
        },
        nodeFctMap() {
            let map = {};

            forEach(this.structure, node => {
                map[node.nodeId] = node.functions && node.functions[0] ? node.functions[0].fctType : undefined;
            });

            return map;
        },
        systemOrganizations() {
            let organizations = [];
            if (this.activeSystem.service_organization) {
                organizations.push({
                    id: this.activeSystem.service_organization.id,
                    name: this.activeSystem.service_organization.name,
                });
                if (this.activeSystem.service_organization.breadcrumbs) {
                    organizations = [...organizations, ...this.activeSystem.service_organization.breadcrumbs];
                }
            }
            return organizations;
        },
    },
    watch: {
        $route: function() {
            this.loadLogs();
            this.loadStructure();
        },
        activeSystem: {
            handler() {
                this.loadLogs();
                this.loadStructure();
            },
        },
    },
    created() {
        this.loadLogs();
        this.loadStructure();
    },
    methods: {
        prevPageHandler() {
            if (this.activeSearchTerm) {
                this.searchLogs(this.activeSearchTerm, this.activePage - 1);
            } else {
                this.loadLogs(this.activePage - 1);
            }
        },
        nextPageHandler() {
            if (this.activeSearchTerm) {
                this.searchLogs(this.activeSearchTerm, this.activePage + 1);
            } else {
                this.loadLogs(this.activePage + 1);
            }
        },
        searchHandler: debounce(function(e) {
            if (e !== '') {
                this.activeSearchTerm = e;
                this.searchLogs(e);
            } else {
                this.activeSearchTerm = false;
                this.searchedLogs = false;
                this.loadLogs();
            }
        }, 300),
        createTextentryHandler() {
            this.$modal.show(
                () => import('@/views/modal/CreateLogTextEntry.vue'),
                {
                    systemId: this.activeSystem.id,
                    organizations: this.systemOrganizations,
                },
                {
                    name: 'CreateLogTextEntry',
                    scrollable: true,
                    width: 450,
                    height: 'auto',
                    class: 'ngdialog-theme-winconnect',
                },
                {
                    'before-close': () => {
                        this.loadLogs();
                        this.loadStructure();
                    },
                }
            );
        },
        async loadLogs(page = 1) {
            if (!this.activeSystem) return;
            try {
                const params = {
                    page: page,
                };
                const response = await api.systems.datapointLogs(this.activeSystem.id, {
                    params,
                });
                if (response && response.data) {
                    this.logs = response.data.logs;
                    this.paginationData = response.data._metadata;
                }
            } catch (error) {
                console.error(error);
                this.$notify({ type: 'error', text: this.$t('errors.error_occurred') });
            }
        },
        async loadStructure() {
            if (!this.activeSystem) return;
            try {
                const response = await api.systems.structure({
                    id: this.activeSystem.id,
                });
                if (response && response.data) {
                    this.structure = response.data.structure;
                }
            } catch (error) {
                console.error(error);
                this.$notify({ type: 'error', text: this.$t('errors.error_occurred') });
            }
        },
        async searchLogs(searchTerm, page = 1) {
            const allowedParams = this.searchFields;
            const searchParams = createSearchParams(searchTerm, allowedParams, 'component');
            const params = {
                ...searchParams,
                page: page,
            };
            try {
                const response = await api.systems.datapointLogsSearch(this.activeSystem.id, {
                    params,
                });
                if (response && response.data) {
                    this.searchedLogs = response.data.logs;
                    this.paginationData = response.data._metadata;
                }
            } catch (e) {
                console.error(e);
            }
        },
        prepareLogs(logs) {
            return logs.map(log => {
                let preparedLog = Object.assign({}, log);
                let fctType = this.nodeFctMap[preparedLog.node_id];

                preparedLog.created_at_formatted = moment(preparedLog.created_at).format('YYYY-MM-DD HH:mm:ss');
                preparedLog.parameter = this.$te('oids.' + preparedLog.oid) ? this.$t('oids.' + preparedLog.oid) : this.$t('oids_oem.' + preparedLog.oid);
                preparedLog.value_formatted = Parameter.formatValue(preparedLog.data, preparedLog.oid, fctType);
                preparedLog.type = Parameter.getType(preparedLog.data, preparedLog.oid, fctType);

                preparedLog.detail = false;

                return preparedLog;
            });
        },
    },
};
</script>

<style lang="scss">
.slide-down-fade-enter,
.slide-down-fade-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}
</style>
